import { z } from 'zod'
import type { AwayReason } from './callback.ts'
import type { TaskCommandType } from './commands.ts'
import type { DueKind, Priority, RequestKind, RequestStatus, TaskStatus } from './domain.ts'
import { PRIORITIES, TASK_STATUSES } from './domain.ts'
import type { TaskDraft } from './draft.ts'

// Контракт HTTP API Mini App (/api/v1). Входные данные — zod-схемы (валидируются на сервере),
// ответы — TS-типы. Все даты в ответах — ISO-строки UTC.

// ───────── Общие ─────────

export interface ApiErrorBody {
  error: {
    code:
      | 'UNAUTHORIZED'
      | 'FORBIDDEN'
      | 'NOT_FOUND'
      | 'VALIDATION'
      | 'INVALID_STATE'
      | 'CONFLICT'
      | 'INTERNAL'
    message: string
  }
}

export interface Paged<T> {
  items: T[]
  nextCursor: string | null
}

export interface UserBrief {
  id: number
  displayName: string
  position: string
  departmentName: string | null
}

// ───────── Профиль и настройки ─────────

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)

export const SettingsPatch = z
  .object({
    timezone: z.string().min(1).max(64),
    workStart: hhmm,
    workEnd: hhmm,
    lunchEnd: hhmm,
    digestTime: hhmm,
    digestEnabled: z.boolean(),
    maxPingsPerDay: z.number().int().min(1).max(30),
    aiDefaultTarget: z.enum(['self', 'ask']),
  })
  .partial()
export type SettingsPatch = z.infer<typeof SettingsPatch>

export interface SettingsDto {
  timezone: string
  workStart: string
  workEnd: string
  lunchEnd: string
  digestTime: string
  digestEnabled: boolean
  maxPingsPerDay: number
  aiDefaultTarget: 'self' | 'ask'
}

export interface MeDto {
  id: number
  displayName: string
  firstName: string
  lastName: string
  position: string
  department: { id: number; name: string } | null
  isAdmin: boolean
  isLeadership: boolean
  /** Есть подчинённые — показываем вкладку «Команда». */
  isManager: boolean
  /** Отмечает начало и конец рабочего дня. */
  shiftsEnabled: boolean
  /** Версия фото профиля (мс); null — фото нет. */
  photoVersion: number | null
  settings: SettingsDto
}

// ───────── Задачи ─────────

export const TASK_SCOPES = ['mine', 'created', 'team', 'watching', 'visible'] as const
export type TaskScope = (typeof TASK_SCOPES)[number]

export const TaskListQuery = z.object({
  scope: z.enum(TASK_SCOPES).default('mine'),
  status: z.enum(TASK_STATUSES).optional(),
  /** Открытые/закрытые: по умолчанию только открытые. */
  state: z.enum(['open', 'closed', 'all']).default('open'),
  due: z.enum(['overdue', 'today', 'week', 'none']).optional(),
  /** Для «Команды»: задачи конкретного сотрудника. */
  userId: z.coerce.number().int().positive().optional(),
  /** Только задачи проекта (среди видимых пользователю). */
  projectId: z.coerce.number().int().positive().optional(),
  q: z.string().max(100).optional(),
  cursor: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
})
export type TaskListQuery = z.input<typeof TaskListQuery>

export interface TaskListItemDto {
  id: number
  title: string
  status: TaskStatus
  priority: Priority
  dueAt: string | null
  dueKind: DueKind
  creator: { id: number; displayName: string }
  /** Основной исполнитель (тот, кто взял задачу). */
  assignee: { id: number; displayName: string }
  /** Остальные исполнители: задача общая, закрыть может любой. */
  coAssignees: { id: number; displayName: string }[]
  isOverdue: boolean
  requiresAcceptance: boolean
  /** Есть открытый вопрос/запрос переноса. */
  pendingRequest: RequestKind | null
  /** Подпункты: сколько готово из скольких (null — подпунктов нет). */
  checklist: { done: number; total: number } | null
  /** Проект или клиент (метка). */
  project: { id: number; name: string } | null
  updatedAt: string
}

// ───────── Люди и процессы ─────────

export const IDEA_STATUSES = ['new', 'review', 'accepted', 'in_progress', 'done', 'rejected'] as const
export type IdeaStatus = (typeof IDEA_STATUSES)[number]

export interface IdeaDto {
  id: number
  title: string
  body: string
  status: IdeaStatus
  anonymous: boolean
  /** null — идея анонимная (автора не видит никто, кроме него самого). */
  author: { id: number; displayName: string } | null
  mine: boolean
  /** Когда планируют внедрить, YYYY-MM-DD. */
  plannedAt: string | null
  implementedAt: string | null
  decisionNote: string | null
  taskId: number | null
  createdAt: string
  updatedAt: string
}

export const IdeaBody = z.object({
  text: z.string().trim().min(5).max(4000),
  anonymous: z.boolean().optional(),
})
export const IdeaStatusBody = z.object({
  status: z.enum(IDEA_STATUSES),
  plannedAt: z.iso.date().nullish(),
  note: z.string().max(1000).nullish(),
})
export const IdeaTaskBody = z.object({
  assigneeId: z.number().int().positive(),
  dueAt: z.iso.datetime({ offset: true }).nullish(),
})

export const APPROVAL_KINDS = ['vacation', 'expense', 'document', 'other'] as const
export type ApprovalKind = (typeof APPROVAL_KINDS)[number]
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export interface ApprovalDto {
  id: number
  kind: ApprovalKind
  title: string
  details: string
  amount: number | null
  dateFrom: string | null
  dateTo: string | null
  status: ApprovalStatus
  author: { id: number; displayName: string }
  steps: {
    step: number
    approver: { id: number; displayName: string }
    decision: 'pending' | 'approved' | 'rejected'
    note: string | null
    decidedAt: string | null
  }[]
  /** Сейчас решение за текущим пользователем. */
  awaitingMe: boolean
  createdAt: string
}

export const ApprovalBody = z
  .object({
    kind: z.enum(APPROVAL_KINDS),
    title: z.string().trim().min(3).max(200),
    details: z.string().max(4000).default(''),
    amount: z.number().int().positive().max(1_000_000_000).nullish(),
    dateFrom: z.iso.date().nullish(),
    dateTo: z.iso.date().nullish(),
  })
  .refine((b) => b.kind !== 'vacation' || (b.dateFrom && b.dateTo && b.dateFrom <= b.dateTo), {
    message: 'Для отпуска укажите даты: с какого по какое число',
  })
  .refine((b) => b.kind !== 'expense' || (b.amount ?? 0) > 0, { message: 'Для расхода укажите сумму' })
export type ApprovalBody = z.input<typeof ApprovalBody>

export const ApprovalDecisionBody = z.object({ approve: z.boolean(), note: z.string().max(1000).nullish() })

export interface ShiftDayDto {
  day: string
  /** Чистое рабочее время: без отсутствий («⏸ Отойти»). */
  workedMinutes: number
  /** Отсутствия внутри рабочего дня. */
  breakMinutes: number
  breaks: number
  overtimeMinutes: number
  late: boolean
  workingDay: boolean
  autoClosed: boolean
}

export interface ShiftStatsDto {
  user: { id: number; displayName: string }
  days: number
  workedMinutes: number
  overtimeMinutes: number
  breakMinutes: number
  lateStarts: number
  autoClosed: number
  byDay: ShiftDayDto[]
  /** Смена идёт сейчас — с какого момента. */
  openSince: string | null
  /** Сейчас отсутствует: причина и когда обещал вернуться (подробности — только себе и руководителю). */
  away: { reason: AwayReason; note: string | null; since: string; until: string } | null
}

export const PeopleFlagsBody = z.object({
  shiftsEnabled: z.boolean().optional(),
  standupEnabled: z.boolean().optional(),
})

export interface ProjectDto {
  id: number
  name: string
  kind: 'project' | 'client'
  archived: boolean
}

export interface ChecklistItemDto {
  id: number
  text: string
  assignee: { id: number; displayName: string } | null
  done: boolean
  doneAt: string | null
}

export interface TaskRequestDto {
  id: number
  kind: RequestKind
  status: RequestStatus
  requestedBy: { id: number; displayName: string }
  proposedDueAt: string | null
  text: string | null
  note: string | null
  createdAt: string
  decidedAt: string | null
}

export interface AttachmentDto {
  id: number
  kind: string
  fileName: string | null
  mime: string | null
  size: number | null
  isResult: boolean
  createdAt: string
}

export type TimelineItemDto =
  | {
      kind: 'event'
      id: string
      at: string
      actor: { id: number; displayName: string } | null
      type: string
      fromStatus: TaskStatus | null
      toStatus: TaskStatus | null
      data: Record<string, unknown>
    }
  | {
      kind: 'comment'
      id: string
      at: string
      actor: { id: number; displayName: string } | null
      commentKind: string
      body: string
    }

export interface TaskDetailDto extends TaskListItemDto {
  description: string | null
  dod: string | null
  /** Откуда задача: подпись, цитата и ссылка на исходное сообщение (если есть). */
  source: { url: string | null; title: string | null; quote: string | null } | null
  requiresResult: boolean
  blockedReason: string | null
  returnedCount: number
  version: number
  assignedAt: string
  acceptedAt: string | null
  startedAt: string | null
  submittedAt: string | null
  doneAt: string | null
  watchers: UserBrief[]
  /** Проверяющий (принимает работу вместо постановщика); null — проверяет постановщик. */
  reviewer: UserBrief | null
  checklistItems: ChecklistItemDto[]
  requests: TaskRequestDto[]
  attachments: AttachmentDto[]
  timeline: TimelineItemDto[]
  /** Что текущий пользователь может сделать (вычислено сервером). */
  allowedActions: TaskCommandType[]
  /** Отношение текущего пользователя к задаче. */
  relations: Array<'creator' | 'assignee' | 'manager' | 'watcher' | 'reviewer'>
  /** Проверка публикации в канале (если включена). */
  publishCheck: PublishCheckDto | null
}

export const PUBLISH_CHECK_STATUSES = ['waiting', 'matched', 'asked', 'disputed', 'escalated'] as const
export type PublishCheckStatus = (typeof PUBLISH_CHECK_STATUSES)[number]

export interface PublishCheckDto {
  channel: ChannelDto | null
  expected: string
  status: PublishCheckStatus
  postLink: string | null
}

/** Канал, где бот — администратор и видит посты. */
export interface ChannelDto {
  id: number
  title: string
  username: string | null
}

export const TaskPatchBody = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  dod: z.string().max(300).nullable().optional(),
  priority: z.enum(PRIORITIES).optional(),
  requiresAcceptance: z.boolean().optional(),
  /** Проверяющий (null — постановщик). */
  reviewerId: z.number().int().positive().nullable().optional(),
  requiresResult: z.boolean().optional(),
  dueAt: z.iso.datetime({ offset: true }).nullable().optional(),
  dueKind: z.enum(['date', 'datetime']).optional(),
  projectId: z.number().int().positive().nullable().optional(),
  /** Оптимистическая блокировка: версия, которую видел клиент. */
  expectedVersion: z.number().int().positive().optional(),
})
export type TaskPatchBody = z.infer<typeof TaskPatchBody>

export const CommentBody = z.object({ text: z.string().trim().min(1).max(4000) })

// ───────── Команда и отчёты ─────────

export interface TeamMemberDto {
  user: UserBrief
  /** Прямой подчинённый (иначе — из глубины ветки). */
  direct: boolean
  open: number
  overdue: number
  awaitingAcceptance: number
  submittedToReview: number
  /** Доля закрытых в срок за 30 дней; null — меньше 5 задач со сроком («мало данных»). */
  onTimeRate30d: number | null
  closed30d: number
  /** Задачи для обсуждения на 1:1 (3+ напоминания о просрочке). */
  oneOnOneCount: number
  /** Место в рейтинге за 30 дней (видит только руководитель); null — мало данных. */
  rank30d: number | null
  /** Версия фото (мс); null — фото нет. Руководитель видит фото своей ветки. */
  photoVersion: number | null
}

export interface WeeklyReportDto {
  weekStart: string
  weekEnd: string
  scope: 'company' | 'team'
  totals: {
    created: number
    closed: number
    onTimeRate: number | null
    openOverdue: number
    awaitingAcceptance: number
  }
  departments: Array<{
    id: number | null
    name: string
    created: number
    closed: number
    onTimeRate: number | null
    openOverdue: number
  }>
  oldestOverdue: Array<{ id: number; title: string; assignee: string; dueAt: string }>
  /** Только для руководителя — по своим прямым подчинённым. */
  people?: Array<{
    user: UserBrief
    closed: number
    onTimeRate: number | null
    openOverdue: number
    /** Место в рейтинге команды (только руководителю); null — мало данных. */
    rank: number | null
    /** Среднее время до принятия задачи, часы. */
    acceptHours: number | null
  }>
}

// ───────── Регулярные задачи ─────────

export interface RecurringRuleDto {
  id: number
  assignee: UserBrief
  template: {
    title: string
    description: string | null
    dod: string | null
    priority: Priority
    requiresAcceptance: boolean
    requiresResult: boolean
    /** Ещё исполнители каждой задачи. */
    coAssignees: UserBrief[]
    /** Проверяющий (null — постановщик). */
    reviewer: UserBrief | null
    /** Проверка публикации у каждой поставленной задачи. */
    publishCheck: { channelId: number | null; expected: string } | null
  }
  schedule: Record<string, unknown>
  /** Человекочитаемо: «каждый пн и чт в 10:00». */
  scheduleText: string
  nextRunAt: string | null
  active: boolean
  /** Последний день повтора (YYYY-MM-DD); null — без окончания. */
  until: string | null
  /** Ближайшие даты постановки (YYYY-MM-DD), до 31. */
  upcoming: string[]
  /** Повтор закончился: дата окончания прошла. */
  finished: boolean
}

/** Предпросмотр расписания: какие дни задача будет ставиться. */
export interface RecurringPreviewDto {
  /** Ближайшие даты (YYYY-MM-DD), до 31. */
  dates: string[]
  /** Сколько всего раз до даты окончания; null — без окончания. */
  total: number | null
  /** Когда поставится первая задача (ISO). */
  firstAt: string | null
}

// ───────── ИИ-черновики ─────────

export interface AiDraftDto {
  id: number
  ord: number
  state: 'pending' | 'creating' | 'created' | 'dropped'
  draft: TaskDraft
  taskId: number | null
  assignee: UserBrief | null
  candidates: UserBrief[]
}

export interface AiBatchDto {
  id: number
  status: 'processing' | 'ready' | 'failed' | 'done' | 'expired'
  source: string
  transcript: string | null
  notes: string | null
  drafts: AiDraftDto[]
  createdAt: string
}

export const DraftPatchBody = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  dod: z.string().max(300).nullable().optional(),
  assigneeId: z.number().int().positive().nullable().optional(),
  dueAt: z.iso.datetime({ offset: true }).nullable().optional(),
  dueKind: z.enum(['date', 'datetime']).optional(),
  priority: z.enum(PRIORITIES).optional(),
  include: z.boolean().optional(),
})
export type DraftPatchBody = z.infer<typeof DraftPatchBody>

export const BatchCommitBody = z.object({
  draftIds: z.array(z.number().int().positive()).min(1).max(10),
  requiresAcceptance: z.boolean().optional(),
})

// ───────── Админка ─────────

export const AdminUserBody = z.object({
  lastName: z.string().trim().max(100).default(''),
  firstName: z.string().trim().min(1).max(100),
  middleName: z.string().trim().max(100).default(''),
  position: z.string().trim().max(200).default(''),
  departmentId: z.number().int().positive().nullable().default(null),
  managerId: z.number().int().positive().nullable().default(null),
  /** Все руководители (первый — основной). Если передан — важнее managerId. */
  managerIds: z.array(z.number().int().positive()).max(5).optional(),
  aliases: z.array(z.string().trim().min(1).max(50)).max(20).default([]),
  isAdmin: z.boolean().default(false),
  isLeadership: z.boolean().default(false),
})
export type AdminUserBody = z.input<typeof AdminUserBody>

export interface AdminUserDto extends UserBrief {
  lastName: string
  firstName: string
  middleName: string
  departmentId: number | null
  /** Основной руководитель. */
  managerId: number | null
  /** Все руководители: основной первым. */
  managerIds: number[]
  aliases: string[]
  isAdmin: boolean
  isLeadership: boolean
  status: 'invited' | 'active' | 'bot_blocked' | 'deactivated'
  hasTelegram: boolean
}

export const DepartmentBody = z.object({
  name: z.string().trim().min(1).max(200),
  parentId: z.number().int().positive().nullable().default(null),
  headUserId: z.number().int().positive().nullable().default(null),
  aliases: z.array(z.string().trim().min(1).max(50)).max(20).default([]),
})

export interface DepartmentDto {
  id: number
  name: string
  parentId: number | null
  headUserId: number | null
  aliases: string[]
  memberCount: number
}

export interface InviteDto {
  userId: number
  /** t.me/<bot>?start=inv_<token> — показываем один раз. */
  link: string
  expiresAt: string
}

export interface HealthDto {
  ok: boolean
  db: boolean
  workerHeartbeatAgeSec: number | null
  outboxLagSec: number | null
  failedNotifications24h: number
  blockedUsers: number
  aiSpendMonthUsd: number | null
}

// ───────── CRM: заметки-стикеры ─────────

export const NOTE_COLORS = ['yellow', 'green', 'blue', 'pink', 'purple', 'gray'] as const
export type NoteColor = (typeof NOTE_COLORS)[number]
/** Не больше стольких заметок у одного человека. */
export const NOTES_LIMIT = 100

export interface NoteDto {
  id: number
  text: string
  color: NoteColor
  /** Задача, созданная из заметки. */
  task: { id: number; title: string; status: TaskStatus } | null
  createdAt: string
  updatedAt: string
}

export const NoteBody = z.object({
  text: z.string().trim().min(1).max(1000),
  color: z.enum(NOTE_COLORS).optional(),
})
export const NotePatchBody = z.object({
  text: z.string().trim().min(1).max(1000).optional(),
  color: z.enum(NOTE_COLORS).optional(),
  taskId: z.number().int().positive().nullish(),
})
export const NoteOrderBody = z.object({ ids: z.array(z.number().int().positive()).max(NOTES_LIMIT) })

// ───────── CRM: фото профиля ─────────

export const PHOTO_MIMES = ['image/jpeg', 'image/webp', 'image/png'] as const
/** Не больше 300 КБ после сжатия в браузере. */
export const PHOTO_MAX_BYTES = 300_000

export const PhotoBody = z.object({
  mime: z.enum(PHOTO_MIMES),
  /** Картинка в base64 (без префикса data:). */
  data: z
    .string()
    .max(Math.ceil((PHOTO_MAX_BYTES * 4) / 3) + 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/),
})

export interface PhotoDto {
  mime: (typeof PHOTO_MIMES)[number]
  data: string
  updatedAt: string
}
